import "./accordion/accordion";
import "./conditional-details/conditional-details";
import "./video/video";
import "./tab/tab";
import "./price-snippet/price-snippet";
import "./trap-focus/trap-focus";
import "./localization-form/localization-form";
import "./aria-listbox/aria-listbox";
import "./cart/cart";
import "./quantity-input/quantity-input";
import "./markup-modal/markup-modal";
import "./product-card/product-card";
import "./relative-link/relative-link";
import "./cookie-banner/cookie-banner";
import "./data-layer/data-layer";
import Swiper from "swiper";
import {
  A11y,
  Autoplay,
  EffectFade,
  Keyboard,
  Navigation,
  Pagination,
  Mousewheel,
  Scrollbar,
  Thumbs,
  Zoom,
  EffectCreative,
} from "swiper/modules";

// Load Swiper once for re-use by any other components
window.loadSwiperFulfillPromise({
  Swiper,
  A11y,
  Autoplay,
  EffectFade,
  EffectCreative,
  Keyboard,
  Navigation,
  Pagination,
  Mousewheel,
  Scrollbar,
  Thumbs,
  Zoom,
});

/**
 * Scrapes a section element for data needing to be handled in section JS
 *
 * @param {DOMNode} container
 * @returns parsed JSON
 */
window.scrapeSectionData = (container) => {
  const dataEl = container.querySelector("[data-section-data]");
  if (!dataEl) return {};

  try {
    return JSON.parse(dataEl.textContent);
  } catch {
    console.warn(`Error parsing data for`, dataEl);
    return {};
  }
};

window.productUrl = (handle) => {
  return `${window.routes.root_url === "/" ? "" : window.routes.root_url}/products/${handle}`;
};

/**
 * Formats a given integer to a standard fraction.
 *
 * @param {Integer} num A price, with cents
 * @param {Integer} fraction
 */
window.formatMoney = (num, showTrailingZeros, currency = "USD") => {
  const fractionDigits = showTrailingZeros
    ? 2
    : (num * 100) % 100 === 0
      ? 0
      : 2;

  return new Intl.NumberFormat("en-EN", {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(num);
};

/**
 * Handleize string
 *
 * @param {string} `products array Object`
 * @returns handleized string object
 *
 */
window.handleize = (string) => {
  string = string.toLowerCase();

  const toReplace = ['"', "'", "\\", "(", ")", "[", "]"];

  // For the old browsers
  for (let i = 0; i < toReplace.length; ++i) {
    string = string.replace(toReplace[i], "");
  }

  string = string.replace(/\W+/g, "-");

  if (string.charAt(string.length - 1) == "-") {
    string = string.replace(/-+\z/, "");
  }

  if (string.charAt(0) == "-") {
    string = string.replace(/\A-+/, "");
  }

  return string;
};

// Resize Shopify Image URL
window.getSizedImageUrl = (src, size) => {
  if (src) {
    const keys = ["width", "height"];
    const params = size
      .split("x")
      .map((value, i) => ({
        key: keys[i],
        value,
      }))
      .filter((param) => param.value)
      .map(({ key, value }) => `${key}=${value}`)
      .join("&");

    return `${src}${src.includes("?") ? "&" : "?"}${params}`;
  } else {
    return "";
  }
};

/**
 * Fetchs page data from alternate templates
 *
 * @param {String} path
 * @param {String} templateSuffix
 * @returns parsed JSON
 */
window.fetchPageData = async ({ path, options = {} }) => {
  if (!path) return {};

  const { templateSuffix, sectionId, variantId } = options;
  const queryArray = [];

  if (templateSuffix) queryArray.push(`view=${templateSuffix}`);
  if (sectionId) queryArray.push(`section_id=${sectionId}`);
  if (variantId) queryArray.push(`variant=${variantId}`);

  const url = `${path}?${queryArray.join("&")}`;
  let html = {};

  await fetch(url)
    .then((response) => response.text())
    .then((response) => {
      const elId = sectionId ? `shopify-section-${sectionId}` : "MainContent";
      html = new DOMParser().parseFromString(response, "text/html");
      html = html.getElementById(elId).innerHTML;
    })
    .catch(() => {
      console.warn(`Error fetching data from ${url}`);
    });

  return html;
};

const lockers = new Set();
let lockedScrollPosition = 0;

window.lockScroll = (locker) => {
  const wasLocked = lockers.size > 0;
  lockers.add(locker);

  if (wasLocked) return;

  // `overflow: hidden` alone does not stop iOS Safari from scrolling the
  // page behind an open modal/drawer via touch, so pin `body` with
  // `position: fixed` and restore the exact scroll offset on unlock.
  lockedScrollPosition = window.scrollY;
  document.documentElement.style.overflow = "hidden";
  document.body.style.position = "fixed";
  document.body.style.top = `-${lockedScrollPosition}px`;
  document.body.style.left = "0";
  document.body.style.width = "100%";
};

window.unlockScroll = (locker) => {
  if (!lockers.delete(locker)) return;

  if (lockers.size) return;

  document.documentElement.style.overflow = null;
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.width = "";
  window.scrollTo({ top: lockedScrollPosition, left: 0, behavior: "instant" });
};

// iOS Safari mis-tracks `visualViewport` after the on-screen keyboard opens
// and closes, leaving `position: fixed`/`sticky` elements (e.g. the sticky
// header) nudged out of place. Once the keyboard fully closes, force a 1px
// scroll nudge so Safari recomputes the viewport.
if (window.visualViewport) {
  let keyboardWasOpen = false;

  window.visualViewport.addEventListener("resize", () => {
    const keyboardIsOpen =
      window.visualViewport.height < window.innerHeight * 0.75;

    if (keyboardWasOpen && !keyboardIsOpen) {
      window.setTimeout(() => {
        if (window.visualViewport.offsetTop !== 0) {
          window.scrollBy(0, -1);
          window.scrollBy(0, 1);
        }
      }, 100);
    }

    keyboardWasOpen = keyboardIsOpen;
  });
}

// Below header dimmer (for mega menus, search, etc)
(() => {
  const openers = new Set();

  const dimmer = document.querySelector(".below-header-dimmer");

  window.openDimmer = (opener) => {
    openers.add(opener);
    dimmer.classList.add("dimmer--active");
  };

  window.closeDimmer = (opener) => {
    openers.delete(opener);

    if (!openers.size) {
      dimmer.classList.remove("dimmer--active");
    }
  };
})();

// Full screen dimmer (for drawers, etc)
(() => {
  const openers = new Set();

  const dimmer = document.querySelector(".full-screen-dimmer");

  window.openFullDimmer = (opener) => {
    openers.add(opener);
    dimmer.classList.add("dimmer--active");
  };

  window.closeFullDimmer = (opener) => {
    openers.delete(opener);

    if (!openers.size) {
      dimmer.classList.remove("dimmer--active");
    }
  };
})();

// Full screen dimmer layered beneath header (needed due to mobile-menu layout within header and z-indexs)
(() => {
  const openers = new Set();

  const dimmer = document.querySelector(".mobile-menu-full-screen-dimmer");

  window.openMobileMenuFullDimmer = (opener) => {
    openers.add(opener);
    dimmer.classList.add("dimmer--active");
  };

  window.closeMobileMenuFullDimmer = (opener) => {
    openers.delete(opener);

    if (!openers.size) {
      dimmer.classList.remove("dimmer--active");
    }
  };
})();

(() => {
  const focusFirstElement = () => {
    const firstEl = document
      .querySelector("#MainContent")
      .querySelector("a,button,input");

    if (firstEl) {
      firstEl.focus();
    }
  };

  const url = new URL(window.location);

  if (url.hash === "#main") {
    focusFirstElement();
  }

  document.addEventListener("click", (e) => {
    const skipLink = e.target.closest("[data-skip-link]");
    if (skipLink) {
      e.preventDefault();
      focusFirstElement();
    }
  });
})();

// Links to #live-chat open the Olark chat widget instead of navigating
document.addEventListener("click", (e) => {
  const chatLink = e.target.closest('a[href$="#live-chat"]');
  if (!chatLink) return;

  e.preventDefault();

  if (typeof window.olark === "function") {
    window.olark("api.box.expand");
  }
});

window.idToGid = (id, type) => {
  return `gid://shopify/${type}/${id}`;
};

window.gidToId = (id) => {
  return id.replace(/^\D*/, "");
};

window.themeBreakpoints = {
  phone: 640,
  tablet: 768,
  "tablet-wide": 1024,
  "desktop-small": 1280,
  desktop: 1440,
  "desktop-large": 1600,
  "desktop-ultra-large": 1920,
};

// JS translation handler
window.theme.translations = function (stringObject, key, variables = {}) {
  let str = stringObject[key];

  if (!str) return "";

  return str.replace(/{{\s*(\w+)\s*}}/g, (match, varName) => {
    return variables[varName] !== undefined ? variables[varName] : "";
  });
};
